import { Fragment, type KeyboardEvent } from 'react';
import type { Todo } from '../../api/todos';
import { dueTone, niceMax, tickDays, type BurnupPoint, type Counts, type ProjectStatus, type ProjectSummary } from '../../lib/projects';
import { daysBetween, formatDue, hasNotes, inDisplayOrder, isOverdue, MONTHS_SHORT } from '../../lib/todos';
import { initialsOf } from '../../components/SubjectPicker';
import { projectText, type ProjectKey } from '../../i18n/projects';
import { todoText } from '../../i18n/todos';

/**
 * THE PROJECTS TAB'S CELLS (V0.50.2) — presentational, stateless, the `documentsCells.tsx`
 * arrangement: `ProjectsTab.tsx` owns the fetch, the selection and the open rows and hands each
 * piece here what it needs to draw.
 *
 * REUSE, NEVER RESEMBLE. Everything here is composed from vocabulary the app already has: the
 * counts wear `.dt-strip-chip` and `.dt-dot` (the Documents strip, the audit facets), the status
 * pill is the action log's outcome pill, the avatars are `.subj-av`, a to-do row is a `.todorow`
 * with the popup's own `.todo-check`, title, caret and `.todo-notes`. The stacked bar and the
 * two schedule meters are the ONE new drawing — nothing in the app drew a bar before — and they
 * are composed from tokens alone. The burn-up follows the `Pie`/`StatusDonut` idiom: an inline
 * SVG, strokes from tokens, the scale maths beside the shape it draws.
 */

/* ----------------------------------------------------------------------------- status */

const STATUS_KEY: Record<ProjectStatus, ProjectKey> = {
  'not-started': 'statusNotStarted',
  'on-track': 'statusOnTrack',
  'at-risk': 'statusAtRisk',
  late: 'statusLate',
  done: 'statusDone',
};

export function StatusPill({ status, locale, id }: { status: ProjectStatus; locale: string; id?: string }) {
  return (
    <span className={`pj-status pj-status-${status}`} id={id} data-status={status}>
      {projectText(STATUS_KEY[status], locale)}
    </span>
  );
}

/* --------------------------------------------------------------------------------- bar */

/** Done, then overdue, then the rest of the open work — 2px gaps, the mark spec's spacer. */
export function ProgressBar({ counts }: { counts: Counts }) {
  const rest = counts.open - counts.overdue;
  const seg = (n: number, cls: string, label: string) =>
    n ? <span key={cls} className={`pj-bar-seg ${cls}`} style={{ flex: n }} title={`${label}: ${n}`} /> : null;
  return (
    <span
      className="pj-bar"
      role="img"
      aria-label={`${counts.done} done, ${counts.overdue} overdue, ${rest} open`}
      data-done={counts.done}
      data-total={counts.total}
    >
      {seg(counts.done, 'done', 'Done')}
      {seg(counts.overdue, 'overdue', 'Overdue')}
      {seg(rest, 'open', 'Open')}
    </span>
  );
}

/* ------------------------------------------------------------------------------- dates */

export function DueCell({ due, state, today, locale }: { due: string | null; state: string; today: string; locale: string }) {
  if (!due) return <span className="pj-muted">—</span>;
  const tone = dueTone(due, state, today);
  return (
    <span
      className={`pj-due${tone === 'none' ? '' : ` tone-${tone}`}`}
      title={tone === 'bad' ? todoText('overdue', locale) : tone === 'warn' ? projectText('dueSoon', locale) : undefined}
    >
      {formatDue(due, today)}
    </span>
  );
}

/* --------------------------------------------------------------------------- avatars */

export function Avatars({
  keys,
  nameOf,
  locale,
  max = 4,
}: {
  keys: readonly string[];
  nameOf: (key: string) => string;
  locale: string;
  max?: number;
}) {
  if (!keys.length) {
    return (
      <span className="subj-av pj-av-none" title={projectText('unassigned', locale)}>
        ?
      </span>
    );
  }
  const shown = keys.slice(0, max);
  return (
    <span className="pj-avs">
      {shown.map((k) => (
        <span key={k} className={`subj-av${k.startsWith('g:') ? ' grp' : ''}`} title={nameOf(k)}>
          {initialsOf(nameOf(k))}
        </span>
      ))}
      {keys.length > max ? <span className="pj-more">+{keys.length - max}</span> : null}
    </span>
  );
}

/* ----------------------------------------------------------------------------- schedule */

export function Schedule({
  summary: sm,
  today,
  locale,
  mayEdit,
}: {
  summary: ProjectSummary;
  today: string;
  locale: string;
  mayEdit: boolean;
}) {
  const dl = sm.daysLeft;
  const daysText =
    dl === null
      ? null
      : `${Math.abs(dl)} ${projectText(Math.abs(dl) === 1 ? (dl < 0 ? 'dayOver' : 'dayLeft') : dl < 0 ? 'daysOver' : 'daysLeft', locale)}`;
  return (
    <div className="pj-schedule" id="pj-schedule">
      {sm.elapsedPct !== null ? (
        <>
          <div className="pj-meter" data-meter="time">
            <span className="pj-meter-label">{projectText('timeElapsed', locale)}</span>
            <span className="pj-meter-track">
              <span className="pj-meter-fill elapsed" style={{ width: `${sm.elapsedPct}%` }} />
              <span className="pj-meter-today" style={{ left: `${sm.elapsedPct}%` }} title={`${projectText('today', locale)} · ${formatDue(today, today)}`} />
            </span>
            <b className="pj-meter-pct">{sm.elapsedPct}%</b>
          </div>
          <div className="pj-meter" data-meter="work">
            <span className="pj-meter-label">{projectText('workDone', locale)}</span>
            <span className="pj-meter-track">
              <span className="pj-meter-fill done" style={{ width: `${sm.pct}%` }} />
            </span>
            <b className="pj-meter-pct">{sm.pct}%</b>
          </div>
        </>
      ) : (
        <div className="pj-empty pj-noschedule">{projectText(mayEdit ? 'noSchedule' : 'noScheduleReadOnly', locale)}</div>
      )}
      {sm.startDate || sm.dueDate ? (
        <div className="pj-sched-ends">
          <span>
            {projectText('start', locale)} · <b>{sm.startDate ? formatDue(sm.startDate, today) : '—'}</b>
          </span>
          <span className={`pj-daysleft${dl !== null && dl < 0 ? ' tone-bad' : ''}`} id="pj-daysleft">
            {daysText}
          </span>
          <span>
            {projectText('target', locale)} · <b>{sm.dueDate ? formatDue(sm.dueDate, today) : '—'}</b>
          </span>
        </div>
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------------------- to-do rows */

/**
 * ONE RENDERER, TWO HOSTS: a node's own to-dos under its tree row, and a person's to-dos under
 * their row in the Assignees view (with `nodeLabelOf` naming the node each one sits on).
 *
 * The row is the popup's `TodoRow` shape without the editing controls: the same checkbox
 * (disabled, never hidden, when this reader may not tick — "you cannot tick this" and "this has
 * no checkbox" are different sentences), the same expandable title with the caret only when
 * there is a description behind it, the same `.todo-notes` beneath.
 */
export function TodoRows({
  todos,
  today,
  locale,
  mayTick,
  busyId,
  onTick,
  openNotes,
  onToggleNotes,
  nameOf,
  nodeLabelOf,
  emptyText,
}: {
  todos: readonly Todo[];
  today: string;
  locale: string;
  mayTick: (t: Todo) => boolean;
  busyId: string | null;
  onTick: (t: Todo) => void;
  openNotes: ReadonlySet<string>;
  onToggleNotes: (id: string) => void;
  nameOf: (key: string) => string;
  /** When set, each row is prefixed with the node it sits on — the Assignees view. */
  nodeLabelOf?: (nodeId: string) => string;
  emptyText: string;
}) {
  if (!todos.length) return <div className="todo-empty">{emptyText}</div>;
  /* ONE IMPLEMENTATION of "done rows sink, the rest keep the order somebody arranged"
     (V0.51.1). This was a copy of the popup section's own rule, and two surfaces drawing the
     same list in two different orders is a bug nobody reports — people just stop trusting it. */
  const ordered = inDisplayOrder(todos);
  /* Which job a sub-task is a step of. Resolved from the rows already in hand rather than
     shipped, unlike "My tasks": a project's list carries its whole subtree, so the parent IS
     here — and `GET /api/projects` ships facts, never joins, by that route's own rule. */
  const titleOf = new Map(todos.map((t) => [t.id, t.title]));
  return (
    <div className="todolist pj-todos">
      {ordered.map((t) => {
        const done = t.state === 'done';
        const expandable = hasNotes(t);
        const open = openNotes.has(t.id);
        const can = mayTick(t);
        const keys = [...t.assigneeGroupIds.map((id) => `g:${id}`), ...t.assigneeUserIds.map((id) => `u:${id}`)];
        const onKey = (e: KeyboardEvent<HTMLSpanElement>) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          onToggleNotes(t.id);
        };
        return (
          <div className="todoblock" key={t.id} data-todo={t.id}>
            <div className={`todorow pj-todo${done ? ' done' : ''}${open ? ' open' : ''}`}>
              <input
                type="checkbox"
                className="todo-check pj-tick"
                data-todo={t.id}
                checked={done}
                disabled={!can || busyId === t.id}
                title={can ? undefined : todoText('notYours', locale)}
                onChange={() => onTick(t)}
                onClick={(e) => e.stopPropagation()}
              />
              <span
                className={expandable ? 'todo-title expandable' : 'todo-title'}
                role={expandable ? 'button' : undefined}
                tabIndex={expandable ? 0 : undefined}
                aria-expanded={expandable ? open : undefined}
                title={expandable ? todoText('showDescription', locale) : undefined}
                onClick={
                  expandable
                    ? (e) => {
                        e.stopPropagation();
                        onToggleNotes(t.id);
                      }
                    : undefined
                }
                onKeyDown={expandable ? onKey : undefined}
              >
                {expandable ? (
                  <svg className={`todo-caret pj-caret${open ? ' open' : ''}`} viewBox="0 0 24 24" aria-hidden="true">
                    <use href="#i-chev" />
                  </svg>
                ) : null}
                {nodeLabelOf ? <span className="pj-todo-node">{nodeLabelOf(t.nodeId)} ›</span> : null}
                {/* WHICH JOB THIS IS A STEP OF (V0.51.1) — the node prefix's own shape, one
                    level in. A sub-task's title is written to be read under its parent. */}
                {t.parentId && titleOf.has(t.parentId) ? (
                  <span className="pj-todo-node" title={titleOf.get(t.parentId)}>
                    {titleOf.get(t.parentId)} ›
                  </span>
                ) : null}
                {t.title}
              </span>
              {keys.map((k) => (
                <span key={k} className={`subj-av${k.startsWith('g:') ? ' grp' : ''} todo-who`} title={nameOf(k)}>
                  {initialsOf(nameOf(k))}
                </span>
              ))}
              {done && t.completedAt ? (
                <span className="todo-due">
                  {projectText('doneOn', locale)} {formatDue(t.completedAt.slice(0, 10), today)}
                </span>
              ) : t.dueDate ? (
                <span
                  className={`todo-due${isOverdue(t.dueDate, t.state, today) ? ' overdue' : ''}`}
                  title={todoText(isOverdue(t.dueDate, t.state, today) ? 'overdue' : 'due', locale)}
                >
                  {formatDue(t.dueDate, today)}
                </span>
              ) : null}
            </div>
            {expandable && open ? (
              <div className="todo-notes pj-todo-notes" data-todo-notes={t.id}>
                {t.notes}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------------------ burn-up */

/**
 * Created vs completed, cumulative per day. A fixed viewBox scaled to the card's width, as the
 * dashboard's rings are; `vector-effect: non-scaling-stroke` keeps the lines 2px at any scale.
 * Text and grid take theme tokens, so the chart reads in both themes.
 */
export function Burnup({
  points,
  from,
  to,
  dueDate,
  today,
  locale,
}: {
  points: readonly BurnupPoint[];
  from: string;
  to: string;
  dueDate: string | null;
  today: string;
  locale: string;
}) {
  const W = 1200;
  const H = 260;
  const P = { l: 40, r: 16, t: 14, b: 30 };
  const n = points.length;
  const yMax = niceMax(Math.max(0, ...points.map((p) => p.created)));
  const X = (i: number) => P.l + (n > 1 ? i / (n - 1) : 0.5) * (W - P.l - P.r);
  const Y = (v: number) => H - P.b - (v / yMax) * (H - P.t - P.b);
  const f = (v: number) => v.toFixed(1);
  const step = (key: 'created' | 'completed') => {
    let d = `M${f(X(0))} ${f(Y(points[0][key]))}`;
    for (let i = 1; i < n; i++) d += ` H${f(X(i))} V${f(Y(points[i][key]))}`;
    return d;
  };
  const area = `${step('completed')} V${f(Y(0))} H${f(X(0))} Z`;
  const ticks = tickDays(from, to)
    .map((day) => ({ day, i: daysBetween(from, day) }))
    .filter((t) => t.i >= 0 && t.i < n);
  const iToday = daysBetween(from, today);
  const iTarget = dueDate ? daysBetween(from, dueDate) : -1;
  const gridValues = [...new Set([0, 0.25, 0.5, 0.75, 1].map((r) => Math.round(yMax * r)))];
  const last = points[n - 1];
  const tickLabel = (day: string) => `${Number(day.slice(8, 10))} ${MONTHS_SHORT[Number(day.slice(5, 7)) - 1]}`;

  return (
    <svg
      className="pj-burnup"
      id="pj-burnup"
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-labelledby="pj-burnup-title"
      data-days={n}
    >
      <title id="pj-burnup-title">{projectText('burnupAria', locale)}</title>
      <g className="pj-burnup-grid">
        {gridValues.map((v) => (
          <Fragment key={v}>
            <line x1={P.l} x2={W - P.r} y1={f(Y(v))} y2={f(Y(v))} stroke="var(--line)" />
            <text x={P.l - 8} y={f(Y(v) + 4)} textAnchor="end" fontSize="12" fill="var(--ink-dim)">
              {v}
            </text>
          </Fragment>
        ))}
      </g>
      <g className="pj-burnup-ticks">
        {ticks.map((t) => (
          <Fragment key={t.day}>
            <line x1={f(X(t.i))} x2={f(X(t.i))} y1={f(Y(0))} y2={f(Y(0) + 5)} stroke="var(--line-strong)" />
            <text x={f(X(t.i))} y={H - 8} textAnchor="middle" fontSize="12" fill="var(--ink-dim)">
              {tickLabel(t.day)}
            </text>
          </Fragment>
        ))}
      </g>
      {iTarget >= 0 && iTarget < n ? (
        <g className="pj-burnup-target">
          <line x1={f(X(iTarget))} x2={f(X(iTarget))} y1={P.t} y2={f(Y(0))} stroke="var(--review-in-review)" strokeDasharray="4 3" />
          <text x={f(X(iTarget) - 6)} y={P.t + 10} textAnchor="end" fontSize="12" fill="var(--review-in-review)">
            {projectText('target', locale)}
          </text>
        </g>
      ) : null}
      {iToday >= 0 && iToday < n ? (
        <g className="pj-burnup-today">
          <line x1={f(X(iToday))} x2={f(X(iToday))} y1={P.t} y2={f(Y(0))} stroke="var(--ink-dim)" strokeDasharray="2 3" />
          <text x={f(X(iToday) + 6)} y={P.t + 10} fontSize="12" fill="var(--ink-dim)">
            {projectText('today', locale)}
          </text>
        </g>
      ) : null}
      <path className="pj-burnup-area" d={area} fill="var(--review-approved)" fillOpacity="0.12" />
      <path
        className="pj-burnup-line"
        data-series="created"
        d={step('created')}
        fill="none"
        stroke="var(--accent)"
        strokeWidth="2"
        vectorEffect="non-scaling-stroke"
      >
        <title>{`${projectText('created', locale)}: ${last.created}`}</title>
      </path>
      <path
        className="pj-burnup-line"
        data-series="completed"
        d={step('completed')}
        fill="none"
        stroke="var(--review-approved)"
        strokeWidth="2"
        vectorEffect="non-scaling-stroke"
      >
        <title>{`${projectText('completed', locale)}: ${last.completed}`}</title>
      </path>
      {iToday >= 0 && iToday < n ? (
        <>
          <circle cx={f(X(iToday))} cy={f(Y(points[iToday].created))} r="4" fill="var(--accent)" stroke="var(--panel)" strokeWidth="2" />
          <circle cx={f(X(iToday))} cy={f(Y(points[iToday].completed))} r="4" fill="var(--review-approved)" stroke="var(--panel)" strokeWidth="2" />
        </>
      ) : null}
    </svg>
  );
}
